/* ─────────────────────────────────────────────────────────────────────────────
   PGA Yorkshire — Section Sticky Stacking
   ─────────────────────────────────────────────────────────────────────────────

   HOW TO USE
   ──────────
   Add data-stack to any section's root element. Consecutive data-stack
   sections automatically stack on top of each other as you scroll — each new
   section slides over the previous one like a card being dealt.

   In a section's Liquid file, on the outermost <div>:
     <div data-stack {{ section.shopify_attributes }}>

   Optional attributes:
     data-stack-radius="20"    corner radius in px (default 16)
     data-stack-scale="0.95"   scale of buried section (default 0.96)
     data-stack-shadow         add a subtle drop shadow to each card

   BEHAVIOUR
   ─────────
   • Each section gets position: sticky; top: 0 so it stays pinned
   • Z-index increments so later sections slide over earlier ones
   • As each new section enters, the buried one scales down slightly
   • Produces a "card deck" depth effect without any JS frameworks
   • Respects prefers-reduced-motion (disables scale animation only)

   TO REMOVE STACKING FROM ONE SECTION
   ────────────────────────────────────
   Remove data-stack from its root element, or add data-stack-skip.
   The engine only groups consecutive data-stack elements.
──────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Collect consecutive runs of [data-stack] siblings ── */
  function findGroups() {
    var all    = Array.prototype.slice.call(document.querySelectorAll('[data-stack]'));
    var groups = [];
    var group  = [];

    all.forEach(function (el, i) {
      if (group.length === 0) {
        group.push(el);
      } else {
        /* Check if this element immediately follows the last (same parent, adjacent) */
        var prev = group[group.length - 1];
        var isAdjacent = prev.parentElement === el.parentElement &&
          prev.nextElementSibling === el;
        if (isAdjacent) {
          group.push(el);
        } else {
          if (group.length > 1) groups.push(group);
          group = [el];
        }
      }
    });
    if (group.length > 1) groups.push(group);
    return groups;
  }

  /* ── Apply stacking styles to one group ── */
  function applyGroup(items) {
    var BASE_Z = 10;

    items.forEach(function (el, i) {
      var radius    = parseFloat(el.dataset.stackRadius)   || 16;
      var hasShadow = el.hasAttribute('data-stack-shadow');

      /* Sticky positioning */
      el.style.position      = 'sticky';
      el.style.top           = '0';
      el.style.zIndex        = BASE_Z + i;
      el.style.transformOrigin = 'top center';

      /* Card styling */
      el.style.borderTopLeftRadius  = radius + 'px';
      el.style.borderTopRightRadius = radius + 'px';
      el.style.overflow = 'hidden';

      if (hasShadow) {
        el.style.boxShadow = '0 -4px 32px rgba(0,0,0,0.14)';
      }
    });

    if (REDUCED_MOTION) return; /* Skip scale animation */

    /* ── Scroll-driven scale effect ── */
    var ticking = false;

    function update() {
      var viewH = window.innerHeight;

      items.forEach(function (el, i) {
        /* Only scale sections that have something coming over them */
        if (i >= items.length - 1) return;

        var next     = items[i + 1];
        var nextTop  = next.getBoundingClientRect().top;
        var maxScale = parseFloat(el.dataset.stackScale) || 0.96;

        /* Progress: 0 when next section hasn't entered, 1 when it's fully over */
        var progress = Math.max(0, Math.min(1, 1 - (nextTop / viewH)));
        var scale    = 1 - (progress * (1 - maxScale));

        el.style.transform = 'scale(' + scale.toFixed(4) + ')';
      });

      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });

    update(); /* Set initial state */
  }

  /* ── Main init ── */
  function init() {
    var groups = findGroups();
    groups.forEach(applyGroup);
  }

  /* ── Startup ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-run on Shopify editor section add/remove */
  document.addEventListener('shopify:section:load',   init);
  document.addEventListener('shopify:section:unload', init);

  window.SectionStack = { refresh: init };
})();
